import { styled } from "styled-components";
import { variables } from "daskapital-ds";

interface IFundingLoanCalculator {
  amount_requested: number;
  term_months: number;
  instalment_freq: string;
  max_apr: number;
  country_code?: string;
  postal_code?: string;
}

interface InterestRate {
  from_day: number;
  to_day: number | null;
  rate: number;
}

interface UnitMap {
  [key: number]: string;
}

export type formsAttrs = {
  getStarted?: {
    url?: string;
    target?: string;
  };
  knowMore?: {
    url?: string;
    target?: string;
  };
  title?: string;
  description?: string;
  backgroundColor?: string;
  textColor?: string;
  termsDescription?: string;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  showNotifyMe?: boolean;
};

export const StyledBubble = styled.div`
  font-family: var(--font-alternative);
  background-color: white;
  color: ${variables.colors.gray90};
  border: 1px solid ${variables.colors.gray10};
  background-color: ${variables.colors.gray10};
  border-radius: 8px;
  min-width: 7rem;
  height: 43px;
  padding: 8px;
  text-align: center;
  display: grid;
  align-content: center;
  margin-bottom: 16px;
`;

/**
 * Returns a predefined array of interest rates based on different time periods.
 * @returns {InterestRate[]} An array of interest rate objects, each containing a from_day, to_day, and rate.
 */
function getInterestRates(): InterestRate[] {
  // Algorithm: Simply return a predefined array of interest rates
  // Each rate object defines a range of days and the corresponding interest rate
  // The last entry with null to_day represents an open-ended range
  const interestRates: InterestRate[] = [
    { from_day: 1, to_day: 30, rate: 0.015 },
    { from_day: 31, to_day: 60, rate: 0.01 },
    { from_day: 61, to_day: 90, rate: 0.01 },
    { from_day: 91, to_day: 180, rate: 0.0075 },
    { from_day: 181, to_day: 365, rate: 0.0075 },
    { from_day: 366, to_day: null, rate: 0.005 }, // null indicates open-ended range
  ];

  return interestRates;
}

/* commission = (30*0.015%) + (30*0.01%) + (30*0.01%) + (10*0.0075%) = 1.125% */
export const calculateInvoiceCommission = (
  invoice_maturity: number,
  gross_total: number
) => {
  const interestRates = getInterestRates();
  let commission = 0;
  let remainingDays = Number(invoice_maturity);

  for (const rate of interestRates) {
    if (remainingDays <= 0) break;

    const periodDays =
      rate.to_day === null
        ? remainingDays
        : Math.min(remainingDays, rate.to_day - rate.from_day + 1);
    commission += periodDays * rate.rate;
    remainingDays -= periodDays;
  }

  return (commission * gross_total) / 100; // Convert percentage to decimal
};

export const calculateInvoiceAmountReceivedNow = ({
  invoice_maturity,
  gross_total,
  max_discount,
}: {
  invoice_maturity: number;
  gross_total: number;
  max_discount: number;
}) => {
  const commission = calculateInvoiceCommission(invoice_maturity, gross_total);
  const amountReceivedNow =
    gross_total -
    (gross_total * max_discount) / 100 -
    gross_total * 0.1 -
    commission;

  return amountReceivedNow;
};

export const calculateInvoiceAmountInvested = ({
  invoice_maturity,
  gross_total,
  max_discount,
}: {
  invoice_maturity: number;
  gross_total: number;
  max_discount: number;
}) => {
  const amountInvested = gross_total * max_discount * (invoice_maturity / 365);

  return amountInvested;
};

export const calculateInvoiceAmountReceived = (gross_total: number) => {
  return 0.1 * gross_total;
};

export const getInstalmentFrequencyValue = (instalment_freq: string) => {
  let inst_freq = 0;
  switch (instalment_freq) {
    case "MONTHLY":
      inst_freq = 12;
      break;
    case "BIMONTHLY":
      inst_freq = 6;
      break;
    case "QUARTERLY":
      inst_freq = 4;
      break;
    case "SEMIYEARLY":
      inst_freq = 2;
      break;
    case "ANNUALLY":
      inst_freq = 1;
      break;
    default:
      break;
  }

  return inst_freq;
};
export const roundDecimalNumber = (num: number) => {
  return Math.round((num + Number.EPSILON) * 100) / 100;
};

/**
 * Calculates the loan instalment amount based on various loan parameters.
 * @param {IFundingLoanCalculator} params - An object containing loan details.
 * @param {string} params.instalment_freq - The frequency of instalments (e.g., 'MONTHLY', 'QUARTERLY').
 * @param {number} params.amount_requested - The total loan amount requested.
 * @param {number} params.term_months - The loan term in months.
 * @param {number} params.max_apr - The maximum annual percentage rate (APR) for the loan.
 * @returns {number} The calculated loan instalment amount, including a commission.
 */
export const calculateLoanAmountInstalment = ({
  instalment_freq,
  amount_requested,
  term_months,
  max_apr,
}: IFundingLoanCalculator): number => {
  // Algorithm:
  // 1. Convert instalment frequency to numerical value
  // 2. Convert APR to decimal
  // 3. Use loan amortization formula to calculate base instalment
  // 4. Round the result
  // 5. Calculate commission (0.5% or minimum 2)
  // 6. Return instalment amount plus commission
  const inst_freq = getInstalmentFrequencyValue(instalment_freq);
  const apr = max_apr / 100;
  const formula =
    (amount_requested * apr) /
    inst_freq /
    (1 - (1 + apr / inst_freq) ** -((term_months * inst_freq) / 12));

  const roundedFormula = roundDecimalNumber(formula);

  return roundedFormula;
};

/**
 * Calculates the maintenance fee for a loan based on the requested amount and instalment frequency.
 * @param {number} amount_requested - The total loan amount requested.
 * @param {string} instalment_freq - The frequency of instalments (e.g., 'MONTHLY', 'QUARTERLY').
 * @returns {number} The calculated maintenance fee for a single period.
 */
export function calculateMaintenanceFee(
  amount_requested: number,
  instalment_freq: string
) {
  const inst_freq = getInstalmentFrequencyValue(instalment_freq);
  return (amount_requested * 0.01) / inst_freq;
}

/**
 * Calculates the total maintenance fee for a loan over its term, considering compounding and APR.
 * @param {number} amount_requested - The total loan amount requested.
 * @param {number} inst_freq - The number of instalment periods per year.
 * @param {number} term_months - The loan term in months.
 * @param {number} max_apr - The maximum annual percentage rate (APR) for the loan.
 * @returns {number} The total maintenance fee over the loan term.
 */
export const calculateTotalMaintenanceFee = (
  amount_requested: number,
  inst_freq: number,
  term_months: number,
  max_apr: number
) => {
  const periodNumber = (term_months * inst_freq) / 12;
  const interestRatePeriod = max_apr / inst_freq / 100;
  const maintenanceFee1 = (amount_requested * 0.01) / inst_freq;

  const maintenanceFee2 =
    periodNumber * (1 + interestRatePeriod) ** periodNumber;
  const maintenanceFee3 =
    ((1 + interestRatePeriod) ** periodNumber - 1) / interestRatePeriod;
  const maintenanceFee4 = (1 + interestRatePeriod) ** periodNumber - 1;

  const maintenanceFee =
    (maintenanceFee1 * (maintenanceFee2 - maintenanceFee3)) / maintenanceFee4;

  return maintenanceFee;
};

/**
 * Calculates the loan instalment amount including taxes and maintenance fee.
 * @param {Object} params - The parameters for the calculation.
 * @param {number} params.amount_installment - The base loan instalment amount.
 * @param {number} params.amount_requested - The total loan amount requested.
 * @param {string} params.instalment_freq - The frequency of instalments (e.g., 'MONTHLY', 'QUARTERLY').
 * @param {string} params.country_code - The country code for VAT calculation.
 * @param {string} params.postal_code - The postal code for VAT calculation.
 * @returns {number} The total instalment amount including maintenance fee and VAT.
 */
export function calculateLoanAmountInstalmentWithTaxes({
  amount_installment,
  amount_requested,
  instalment_freq,
}: {
  amount_installment: number;
  amount_requested: number;
  instalment_freq: string;
}) {
  const maintenanceFee = calculateMaintenanceFee(
    amount_requested,
    instalment_freq
  );

  /*   const vat = calculateVAT(country_code, postal_code); */

  const formula = amount_installment + maintenanceFee;
  return formula;
}

export const getStampTax = (amount_requested: number, term_months: number) => {
  return 0; // Deactivated by the client for now
  let stampTax = amount_requested;
  if (term_months === 6) {
    stampTax *= 0.0024;
  } else if (term_months === 9) {
    stampTax *= 0.0036;
  } else if (term_months >= 12 && term_months < 60) {
    stampTax *= 0.005;
  } else if (term_months >= 60) {
    stampTax *= 0.006;
  }

  return stampTax;
};

const getCommission = (amount_requested: number) => {
  let amount = amount_requested;
  if (amount_requested > 50000) {
    amount *= 0.98;
  } else if (amount_requested > 20000) {
    amount *= 0.975;
  } else if (amount_requested > 10000) {
    amount *= 0.97;
  } else {
    amount *= 0.965;
  }

  return amount;
};
/**
 * Calculates the commission percentage for a loan based on the requested amount.
 * @param {number} amount_requested - The total loan amount requested.
 * @returns {number} The calculated commission percentage.
 */
export function getCommissionPercentage(amount_requested: number): number {
  // Algorithm:
  // Apply different commission rates based on loan amount:
  // - 2.5% for > 150,000
  // - 3% for > 50,000
  // - 3.5% for > 15,000
  // - 4% for <= 15,000
  // Return the commission amount
  let amount = amount_requested;
  if (amount_requested > 150000) {
    amount *= 0.025;
  } else if (amount_requested > 50000) {
    amount *= 0.03;
  } else if (amount_requested > 15000) {
    amount *= 0.035;
  } else {
    amount *= 0.04;
  }

  // If the commission amount is less than 250, return minimum tax of250
  return amount < 250 ? 250 : amount;
}

/**
 * Calculates the VAT (Value Added Tax) rate based on country and postal code.
 * @param {string} country_code - The country code (e.g., 'PT' for Portugal).
 * @param {string} postal_code - The postal code to determine the VAT region.
 * @returns {number} The VAT rate as a decimal (e.g., 0.23 for 23%).
 */
function calculateVAT(country_code: string, postal_code: string) {
  const postalCode = Number(postal_code.slice(0, 4));
  if (country_code !== "PT") {
    return 0;
  }

  if (postalCode < 9000) {
    return 0.23;
  }
  if (postalCode > 9000 && postalCode < 9500) {
    return 0.22;
  }
  if (postalCode > 9500) {
    return 0.16;
  }

  return 0;
}

/**
 * Calculates the actual amount received for a loan, considering stamp tax and commission.
 * @param {number} amount_requested - The total loan amount requested.
 * @param {number} term_months - The loan term in months.
 * @returns {number} The calculated amount received after deducting stamp tax and commission.
 */
export const calculateLoanAmountReceived = (
  amount_requested: number,
  term_months: number
): number => {
  // Algorithm:
  // 1. Calculate stamp tax
  // 2. Calculate amount after commission
  // 3. Subtract stamp tax from amount after commission
  // 4. Round the result to two decimal places
  const stampTax = getStampTax(amount_requested, term_months);

  const commissionAmount = getCommissionPercentage(amount_requested);
  const amount = amount_requested - commissionAmount;

  const result = roundDecimalNumber(
    roundDecimalNumber(amount) - roundDecimalNumber(stampTax)
  );

  return result;
};

/**
 * Calculates the total payment amount for a loan, including instalments, stamp tax, and commission.
 * @param {IFundingLoanCalculator} params - An object containing loan details.
 * @param {string} params.instalment_freq - The frequency of instalments (e.g., 'MONTHLY', 'QUARTERLY').
 * @param {number} params.amount_requested - The total loan amount requested.
 * @param {number} params.term_months - The loan term in months.
 * @param {number} params.max_apr - The maximum annual percentage rate (APR) for the loan.
 * @returns {number} The calculated total payment amount for the loan.
 */
export const calculateLoanTotalPayment = ({
  instalment_freq,
  amount_requested,
  term_months,
  max_apr,
}: IFundingLoanCalculator): number => {
  // Algorithm:
  // 1. Calculate loan instalment amount
  // 2. Calculate total instalments: instalment * (term_months * frequency / 12)
  // 3. Add stamp tax
  // 4. Add commission percentage
  // 5. Round the total to two decimal places
  const loanAmountInstalment = calculateLoanAmountInstalment({
    instalment_freq,
    amount_requested,
    term_months,
    max_apr,
  });
  const inst_freq = getInstalmentFrequencyValue(instalment_freq);
  const totalCost = (loanAmountInstalment * (term_months * inst_freq)) / 12;
  const stampTax = getStampTax(amount_requested, term_months);
  const processingFee = getCommissionPercentage(amount_requested);

  const maintenanceFee = calculateTotalMaintenanceFee(
    amount_requested,
    inst_freq,
    term_months,
    max_apr
  );

  const value = totalCost + stampTax + processingFee + maintenanceFee;

  return roundDecimalNumber(value);
};

export const formatMonthsToHuman = (
  months: number,
  dict?: Record<string, string>,
) => {
  if (!months) return 0;

  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;

  const monthsLabel = dict?.months || "months";
  const yearLabel = dict?.year || "year";
  const yearsLabel = dict?.years || "years";
  const halfYearLabel = dict?.halfYear || "Half a year";
  const andLabel = dict?.and || "and";

  const units: UnitMap = {
    0: `${months} ${monthsLabel}`,
    1: `${years} ${years > 1 ? yearsLabel : yearLabel}`,
  };

  // Check for special case of 6 months
  if (months === 6) {
    units[6] = halfYearLabel;
  }

  return `${units[years] || units[1]} ${years > 0 && remainingMonths !== 0 ? `${andLabel} ${remainingMonths} ${monthsLabel}` : ""}`; // Fallback for years > 1
};

export const validateStringData = (
  str: string | number | null | undefined,
  fallback: string = ""
) => {
  if (!str) return fallback;
  return str;
};

/**
 * Converts a string to camel case
 * @param {string} string - The string to convert
 * @returns {string} The string in camel case
 */
export function camelCase(string: string) {
  return (
    string.toLowerCase().charAt(0).toUpperCase() + string.slice(1).toLowerCase()
  );
}

/**
 * Formats a number as a currency string in Euros.
 * @param value - The number to format as currency.
 * @returns A string representing the value formatted as Euros, or '-' if the input is invalid.
 */
export function formatAsCurrency(value: number): string {
  if (!value && value !== 0) return "-";

  const Euro = new Intl.NumberFormat("pt-pt", {
    style: "currency",
    currency: "Eur",
    useGrouping: true,
  });

  return Euro.format(value);
}

/* --- Rent advance simulator (Dasrent) --- */

interface IRentAdvanceInvestorCalculator {
  amount: number;
  numberOfRents: number;
  rentFrequency: string;
  minReturnRate: number;
}

interface IRentAdvancePromoterCalculator {
  numberOfRents: number;
  rentValue: number;
  discount: number;
}

/**
 * Number of calendar months in a single instalment period, matching the
 * Excel model's "E12" column (Monthly=1, Bimonthly=2, Quarterly=3, Semiyearly=6, Yearly=12).
 * Derived from getInstalmentFrequencyValue (periods per year) instead of a second
 * independent mapping, so the two can't drift apart if a frequency is ever added.
 */
export const getRentFrequencyPeriodMonths = (rentFrequency: string) => {
  const periodsPerYear = getInstalmentFrequencyValue(rentFrequency);
  return periodsPerYear ? 12 / periodsPerYear : 0;
};

/**
 * Present value of an ordinary annuity (mirrors Excel's PV(rate, nper, pmt, 0, 0)
 * with a negative pmt), used to find the fair upfront bonus in the investor formula.
 */
const presentValueAnnuity = (
  ratePerPeriod: number,
  periods: number,
  paymentPerPeriod: number
) => {
  if (ratePerPeriod === 0) return paymentPerPeriod * periods;
  return (
    (paymentPerPeriod * (1 - Math.pow(1 + ratePerPeriod, -periods))) /
    ratePerPeriod
  );
};

/** Number of months the investor's money is committed for (Excel "L2"). */
export const calculateRentInvestorTotalMonths = ({
  numberOfRents,
  rentFrequency,
}: Pick<
  IRentAdvanceInvestorCalculator,
  "numberOfRents" | "rentFrequency"
>) => {
  return numberOfRents * getRentFrequencyPeriodMonths(rentFrequency);
};

/** Amount paid back to the investor on each rent period (Excel "O10" = amount / numberOfRents). */
export const calculateRentInvestorPeriodPayment = ({
  amount,
  numberOfRents,
}: Pick<IRentAdvanceInvestorCalculator, "amount" | "numberOfRents">) => {
  if (!numberOfRents) return 0;
  return amount / numberOfRents;
};

/**
 * Extra amount paid to the investor upfront (Excel "K8") so that the whole cash
 * flow (this bonus now + the period payments) yields exactly `minReturnRate` (APR).
 * K8 = amount - PV(minReturnRate/periodsPerYear, numberOfRents, -periodPayment)
 */
export const calculateRentInvestorUpfrontBonus = ({
  amount,
  numberOfRents,
  rentFrequency,
  minReturnRate,
}: IRentAdvanceInvestorCalculator) => {
  const periodsPerYear = getInstalmentFrequencyValue(rentFrequency);
  if (!periodsPerYear || !numberOfRents) return 0;

  const periodPayment = calculateRentInvestorPeriodPayment({
    amount,
    numberOfRents,
  });
  const ratePerPeriod = minReturnRate / 100 / periodsPerYear;
  const fairValue = presentValueAnnuity(
    ratePerPeriod,
    numberOfRents,
    periodPayment
  );

  return amount - fairValue;
};

/** Total nominal amount received by the investor: upfront bonus + all period payments (Excel "L4"). */
export const calculateRentInvestorTotalReceived = (
  params: IRentAdvanceInvestorCalculator
) => {
  const upfrontBonus = calculateRentInvestorUpfrontBonus(params);
  const periodPayment = calculateRentInvestorPeriodPayment(params);

  return upfrontBonus + params.numberOfRents * periodPayment;
};

/** Total gross value of the rents being advanced (Excel "O3" = numberOfRents * rentValue). */
export const calculateRentPromoterTotalAmount = ({
  numberOfRents,
  rentValue,
}: Pick<IRentAdvancePromoterCalculator, "numberOfRents" | "rentValue">) => {
  return numberOfRents * rentValue;
};

/**
 * Service fee charged to the promoter: a tiered percentage of the total advanced
 * amount (rentValue * numberOfRents), with a 150€ minimum:
 * - up to 50.000€: 3,5%
 * - up to 150.000€: 3,0%
 * - above 150.000€: 2,5%
 */
export const calculateRentPromoterServiceFee = ({
  numberOfRents,
  rentValue,
}: Pick<IRentAdvancePromoterCalculator, "numberOfRents" | "rentValue">) => {
  const totalAmount = rentValue * numberOfRents;
  let rate = 0.025;
  if (totalAmount <= 50000) rate = 0.035;
  else if (totalAmount <= 150000) rate = 0.03;

  return Math.max(150, rate * totalAmount);
};

/**
 * Maximum discount taken from the promoter, across all advanced rents
 * (numberOfRents * rentValue * discount). The source spreadsheet's "O13" applied the
 * discount to a single rent's value instead of the full advanced amount — corrected
 * here so it scales the same way "You will receive at least" (O5) already does.
 */
export const calculateRentPromoterMaxDiscount = ({
  numberOfRents,
  rentValue,
  discount,
}: Pick<
  IRentAdvancePromoterCalculator,
  "numberOfRents" | "rentValue" | "discount"
>) => {
  return numberOfRents * rentValue * (discount / 100);
};

/** Maximum total cost to the promoter: discount + service fee (Excel "O11"). */
export const calculateRentPromoterMaxTotalCost = (
  params: IRentAdvancePromoterCalculator
) => {
  return (
    calculateRentPromoterMaxDiscount(params) +
    calculateRentPromoterServiceFee(params)
  );
};

/**
 * Minimum amount the promoter will receive after discount and service fee
 * (Excel "O5" = numberOfRents * rentValue * (1 - discount) - serviceFee).
 */
export const calculateRentPromoterMinReceived = (
  params: IRentAdvancePromoterCalculator
) => {
  const { numberOfRents, rentValue, discount } = params;
  const totalAmount = calculateRentPromoterTotalAmount({
    numberOfRents,
    rentValue,
  });
  const serviceFee = calculateRentPromoterServiceFee({
    numberOfRents,
    rentValue,
  });

  return totalAmount * (1 - discount / 100) - serviceFee;
};
